import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { format } from '../modules/helpers';
import * as d3 from 'd3';
import clusterAggregate from './singleClusterProjectionsLineGraph/clusterAggregate';
import { isSchoolSmall } from './singleClusterProjectionsLineGraph/isSmallProjectedNode'; 

function makeTableRow(data, colcb) {
  let tr = document.createElement('tr');
  data.forEach((d, i) => {
    colcb(tr, d, i);
  });
  return tr;
}

function makeInactiveSchoolsTable(unsortedData) {
  const data = unsortedData.sort((a, b) => {
    return a.name < b.name ? -1 : 1
  })
  const inactiveSchools = data.filter(d => !d.isActive);
  const activeSchool = data.find(d => d.isActive);
  const firstLabel = activeSchool
    ? 'Percentage change in other schools'
    : 'Percentage change per school';
  const colLabels = [firstLabel, ...inactiveSchools[0].projections_years];
  // The 3rd column is always the "baseline" for enrolment variance
  colLabels[2] = `${colLabels[2]} (baseline)`;

  const table = document.createElement('table');
  const tablehead = document.createElement('thead');
  const tablebody = document.createElement('tbody');

  table.setAttribute(
    'summary',
    'A table of figures showing projected % change in student population for all clusters. Shows percentage schools group name on left and time in 5 year increments across the top.'
  );

  let tr = makeTableRow(colLabels, (tr, d, i) => {
    let th = document.createElement('th');
    th.innerHTML = d;
    tr.appendChild(th);
  });
  tablehead.appendChild(tr);
  table.append(tablehead);

  inactiveSchools.forEach(school => {
    const isThisSchoolSmall = isSchoolSmall(school); 
    let rowdata = [];
    rowdata.push(
      process.env.NODE_ENV === 'development'
        ? `<a href="?school=${school.uid}">${school.name}</a>`
        : school.name
    );

    for (let i = 0; i < school.projections_percentage.length; i++) {
       if (isThisSchoolSmall) { 
        rowdata.push('<50'); 
      } else { 
        const percentage = school.projections_percentage[i]; 
        const count = school.projections[i];
        const prefix = percentage > 0 ? '+' : ''
        // First two years we want to show the raw number.
        const d = i <= 1 ? count : prefix + format(percentage, 0) + '%'; 
        rowdata.push(d); 
      } 
    }

    let tr = makeTableRow(rowdata, (tr, d, i) => {
      let td = document.createElement('td');
      if (i === 0) {
        td = document.createElement('th');
      }
      td.innerHTML = d;
      tr.appendChild(td);
    });
    tablebody.appendChild(tr);
  });

  table.appendChild(tablebody);
  table.classList.add('summary-table');

  return table;
}

function makeActiveSchoolTable(schools, isMetro, activeCluster) {
  let activeSchool = schools.find(d => d.isActive);
  if (!activeSchool) {
    // Just return nothing for now. Later we should probably return a cluster summary
    // return document.createElement('div');
    activeSchool = clusterAggregate(schools, activeCluster);
    activeSchool.name = 'Schools Group Total'; // Rather than 'Cluster Average'
  }
  const isThisSchoolSmall = isSchoolSmall(activeSchool); 
  const colLabels = [activeSchool.name, ...activeSchool.projections_years];
  // The 3rd column is always the "baseline" for enrolment variance
  colLabels[2] = `${colLabels[2]} (baseline)`;

  const table = document.createElement('table');
  const tablehead = document.createElement('thead');
  const tablebody = document.createElement('tbody');

  table.setAttribute(
    'summary',
    `A table of figures showing projected change in student population for ${activeSchool.name}. Shows percentage change and student population on the left and time in 5 year increments across the top.`
  );

  let tr = makeTableRow(colLabels, (tr, d, i) => {
    let th = document.createElement('th');
    th.innerHTML = d;
    tr.appendChild(th);
  });
  tablehead.appendChild(tr);
  table.append(tablehead);

  let rowdata = [];
  rowdata.push('Percentage change');

  for (let i = 0; i < activeSchool.projections_years.length; i++) {
    if (isThisSchoolSmall) { 
      rowdata.push('-'); 
    } else { 
      const percentage = activeSchool.projections_percentage[i]; 
      const prefix = percentage > 0 ? '+' : ''
      const d = i <= 1 ? '-' : prefix + format(percentage, 0) + '%'; 
      rowdata.push(d);
    } 
  }

  tr = makeTableRow(rowdata, (tr, d, i) => {
    let td = document.createElement('td');
    if (i === 0) {
      td = document.createElement('th');
    }
    td.innerHTML = d;
    tr.appendChild(td);
  });
  tablebody.appendChild(tr);

  rowdata = [];

  if (isMetro) {
    rowdata.push('Student population');
  } else {
    rowdata.push('Enrolments');
  }

  for (let i = 0; i < activeSchool.projections_years.length; i++) {
    if (isThisSchoolSmall) { 
      rowdata.push('<50');
    } else {
      const percentage = activeSchool.projections[i];
      rowdata.push(format(percentage, 0));
    }
  }
  tr = makeTableRow(rowdata, (tr, d, i) => {
    let td = document.createElement('td');
    if (i === 0) {
      td = document.createElement('th');
    }
    td.innerHTML = d;
    tr.appendChild(td);
  });
  tablebody.appendChild(tr);

  table.appendChild(tablebody);
  table.classList.add('summary-table');
  table.classList.add('current-cluster');

  return table;
}

function makeProjectionsTable(activeSchool, activeCluster, isMetro) {
  const schools = activeCluster.schools.filter(d =>
    Array.isArray(d.projections)
  );

  const clusterTable = makeInactiveSchoolsTable(schools);
  const summaryTable = makeActiveSchoolTable(schools, isMetro, activeCluster);

  const container = document.createElement('div');
  container.appendChild(summaryTable);
  container.appendChild(clusterTable);
  container.classList.add('scroll-container');

  const pane = document.createElement('div');
  pane.appendChild(container);

  d3
    .select(pane)
    .append('div')
    .style('font-size', '11px')
    .text('Data subject to change.*');

  return pane;
}

class ClusterProjections extends Component {
  wrapChart() {
    this.container = findDOMNode(this);
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }
    const { data, school, isMetro } = this.props;
    const chart = makeProjectionsTable(school, data, isMetro);
    this.container.appendChild(chart);
  }

  componentDidMount() {
    this.wrapChart();
  }
  componentDidUpdate() {
    this.wrapChart();
  }

  render() {
    return <div />;
  }
}

export default ClusterProjections;



// WEBPACK FOOTER //
// ./src/components/singleClusterProjectionsTable.js